/**
 * Pure presentation helpers shared across the calls slice.
 *
 * Everything here maps the CANONICAL Hostesa call model (from
 * `@hostesa/api-client` response schemas) to display primitives. No
 * provider-specific (Vapi/LiveKit) field is read, branched on, or rendered.
 */

import type { CallStatus } from '@hostesa/api-client';
import type { StatusVariant } from '@hostesa/ui';

/**
 * Maps a canonical call status to a {@link StatusVariant}. The variant only
 * controls the badge dot/text color — the human-readable label always carries
 * the meaning (never color-only).
 */
export function callStatusVariant(status: CallStatus): StatusVariant {
  switch (status) {
    case 'ai_active':
    case 'human_active':
    case 'answered':
      return 'active';
    case 'ringing':
    case 'created':
      return 'pending';
    case 'handoff_requested':
      return 'warning';
    case 'completed':
      return 'success';
    case 'failed':
      return 'error';
    default:
      return 'neutral';
  }
}

/** Statuses that represent a call that is still on the line. */
const ACTIVE_STATUSES: ReadonlySet<CallStatus> = new Set<CallStatus>([
  'created',
  'ringing',
  'answered',
  'ai_active',
  'handoff_requested',
  'human_active',
]);

/** True when the call is still in progress (eligible for the live panel). */
export function isCallActive(status: CallStatus): boolean {
  return ACTIVE_STATUSES.has(status);
}

/** True only while a handoff may still be requested (call is active). */
export function canRequestHandoff(status: CallStatus): boolean {
  return isCallActive(status) && status !== 'handoff_requested' && status !== 'human_active';
}

/**
 * Coerces the permissive `language` field (the schema models it as
 * `unknown | null`) to a display string, falling back to a placeholder.
 */
export function languageLabel(language: unknown, fallback: string): string {
  if (typeof language === 'string' && language.trim().length > 0) {
    const code = language.trim();
    try {
      const display = new Intl.DisplayNames([code], { type: 'language' }).of(code);
      if (display && display.toLowerCase() !== code.toLowerCase()) {
        return `${display} (${code.toUpperCase()})`;
      }
    } catch {
      // Intl.DisplayNames can throw on malformed tags — fall through to the code.
    }
    return code.toUpperCase();
  }
  return fallback;
}

/** Upper-cased language code for a compact pill, or an em dash when absent. */
export function languagePillCode(language: unknown): string {
  return typeof language === 'string' && language.trim() ? language.trim().toUpperCase() : '—';
}

/** Coerces the permissive `durationSeconds` field to a number, or null. */
export function durationSeconds(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

/** Coerces a permissive scalar (e.g. `endReason`) to a trimmed string or null. */
export function optionalText(value: unknown): string | null {
  return typeof value === 'string' && value.trim().length > 0 ? value.trim() : null;
}

/** Truncates a UUID for compact display (e.g. `1a2b3c4d…`). */
export function shortId(id: string): string {
  return id.length > 8 ? `${id.slice(0, 8)}…` : id;
}

/**
 * Fallback display for a canonical snake_case tool name when no i18n label
 * exists for it (e.g. `flag_handoff` → `Flag handoff`). The page prefers a
 * translated label and only falls back to this for unknown tool names.
 */
export function humanizeToolName(name: string): string {
  const spaced = name.trim().replace(/_/g, ' ');
  return spaced.length > 0 ? spaced.charAt(0).toUpperCase() + spaced.slice(1) : name;
}
